// Utility functions for formatting job data

/**
 * Format a datetime to relative time (e.g., "2 hours ago", "in 3 hours")
 */
export function formatRelativeTime(datetime) {
  if (!datetime) return '-';
  
  const date = new Date(datetime);
  const now = new Date();
  const diffMs = date - now;
  const diffSec = Math.abs(diffMs) / 1000;
  
  const isPast = diffMs < 0;
  
  if (diffSec < 60) {
    return isPast ? 'Just now' : 'Soon';
  }
  
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    const unit = diffMin === 1 ? 'minute' : 'minutes';
    return isPast ? `${diffMin} ${unit} ago` : `in ${diffMin} ${unit}`;
  }
  
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) {
    const unit = diffHour === 1 ? 'hour' : 'hours';
    return isPast ? `${diffHour} ${unit} ago` : `in ${diffHour} ${unit}`;
  }
  
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) {
    const unit = diffDay === 1 ? 'day' : 'days';
    return isPast ? `${diffDay} ${unit} ago` : `in ${diffDay} ${unit}`;
  }
  
  const diffWeek = Math.floor(diffDay / 7);
  if (diffWeek < 4) {
    const unit = diffWeek === 1 ? 'week' : 'weeks';
    return isPast ? `${diffWeek} ${unit} ago` : `in ${diffWeek} ${unit}`;
  }
  
  return date.toLocaleDateString();
}

/**
 * Parse cron expression to human-readable format
 */
export function parseCronExpression(cronExpr) {
  if (!cronExpr) return 'Manual';
  
  // Basic cron parsing - could be enhanced with a library
  const parts = cronExpr.split(' ');
  if (parts.length < 5) return cronExpr;
  
  const [minute, hour, dayOfMonth, , dayOfWeek] = parts;
  
  // Common patterns
  if (cronExpr === '*/5 * * * *') return 'Every 5 minutes';
  if (cronExpr === '0 * * * *') return 'Every hour';
  if (cronExpr === '0 9 * * 1-5') return 'Weekdays at 9 AM';
  if (cronExpr === '0 0 * * *') return 'Daily at midnight';
  if (cronExpr === '0 */6 * * *') return 'Every 6 hours';
  if (cronExpr === '*/15 * * * *') return 'Every 15 minutes';
  
  // General patterns
  if (minute === '0' && hour !== '*') {
    const hourNum = parseInt(hour);
    const time = hourNum === 0 ? 'midnight' : hourNum < 12 ? `${hourNum} AM` : hourNum === 12 ? 'noon' : `${hourNum - 12} PM`;
    
    if (dayOfWeek === '1-5') return `Weekdays at ${time}`;
    if (dayOfWeek === '*' && dayOfMonth === '*') return `Daily at ${time}`;
    if (dayOfWeek === '0' || dayOfWeek === '7') return `Sundays at ${time}`;
    if (dayOfWeek === '1') return `Mondays at ${time}`;
  }
  
  // Fallback to showing the expression
  return cronExpr;
}

/**
 * Get status icon and color based on completion status
 */
export function getStatusInfo(completionStatus) {
  if (!completionStatus) {
    return { color: '#999', label: 'Never Executed' };
  }
  
  switch (completionStatus) {
    case 'EXECUTION_SUCCEEDED':
      return { icon: '✓', color: '#4caf50', label: 'Success' };
    case 'EXECUTION_FAILED':
      return { icon: '✗', color: '#f44336', label: 'Failed' };
    case 'EXECUTION_RUNNING':
      return { icon: '⏳', color: '#2196f3', label: 'Running' };
    case 'EXECUTION_CANCELLED':
      return { icon: '⊘', color: '#ff9800', label: 'Cancelled' };
    default:
      return { icon: '•', color: '#999', label: completionStatus };
  }
}

/**
 * Calculate duration between two dates
 */
export function calculateDuration(startTime, endTime) {
  if (!startTime || !endTime) return null;
  
  const start = new Date(startTime);
  const end = new Date(endTime);
  const diffMs = end - start;
  
  if (diffMs < 0) return null;
  
  const diffSec = Math.floor(diffMs / 1000);
  
  if (diffSec < 60) {
    return `${diffSec}s`;
  }
  
  const minutes = Math.floor(diffSec / 60);
  const seconds = diffSec % 60;
  
  if (minutes < 60) {
    return `${minutes}m ${seconds}s`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Format full datetime for display
 */
export function formatDateTime(datetime) {
  if (!datetime) return '-';
  
  const date = new Date(datetime);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}
